import { Controlled as CodeMirror } from 'react-codemirror2';
import type { VoidFunctionComponent } from 'react';

import CliLicenseScannedMatches from '../../../../licenses/CliLicenseScannedMatches';
import FileContentsLoading from '../FileContentsLoading';
import useRevisionFileBlobForCodemirror from '../hooks/useRevisionFileBlobForCodemirror';
import type { RevisionFileMatch } from '../../../../../hooks/api/revisions/useFileMatchesForRevision';

import FileContentsNotFound from './FileContentsNotFound';
import { getHighlightOptions, jumpToLineNumber } from './utils';

import styles from '../FileViewer.module.less';

/**
 * Trying to display files that are too large will crash the browser tab.
 * Instead, limit the size of files that can be shown. In their place,
 * show a simple "file is too large to show" message. We know that 4MB
 * is too large, so we'll keep the size limit at 1MB (to be safe).
 */
export const FILE_VIEWER_SIZE_LIMIT = 1024 * 1024;

type Props = {
  //  Locator for revision, including the version.
  revisionLocator: string;
  //  File path within the revision.
  filePath: string;
  //  Where the matches were found in the file, if anywhere.
  sources: RevisionFileMatch['sources'];
  // Check if the license data is from cli scan.
  isCliLicenseScanWithMatchData: boolean;
};

/**
 * FileViewerFileContents is a wrapper around Codemirror.
 *
 * Fetches a file blob for a given revision + file path,
 * then displays it in a read-only Codemirror container.
 *
 * Attempts to use the
 */
const FileViewerFileContents: VoidFunctionComponent<Props> = ({
  revisionLocator,
  filePath,
  sources,
  isCliLicenseScanWithMatchData,
}) => {
  const {
    mode,
    error,
    isError,
    isLoading,
    fileContents,
    isNotFound,
  } = useRevisionFileBlobForCodemirror(revisionLocator, filePath);

  if (isLoading) {
    return <FileContentsLoading />;
  }

  //  Not found errors are handled separately,
  //  as we display a "reanalyze dependency" button.
  if (isNotFound) {
    return (
      <FileContentsNotFound revisionLocator={revisionLocator} />
    );
  }

  //  Display any other errors.
  if (isError) {
    return (
      <div className={styles.error}>
        {error?.toString() || error}
      </div>
    );
  }

  if (isCliLicenseScanWithMatchData && fileContents) {
    return (
      <div className={styles.fileContents}>
        <CliLicenseScannedMatches
          licenseMatchJson={fileContents}
        />
      </div>
    );
  }

  const fileSize = fileContents?.length ?? 0;
  if (fileSize > FILE_VIEWER_SIZE_LIMIT) {
    return (
      <div className={styles.error}>
        This file is too large to be displayed.
      </div>
    );
  }

  return (
    <div className={styles.fileContents}>
      <CodeMirror
        className={styles.codeMirror}
        value={fileContents || ''}
        options={{
          mode,
          lineNumbers: true,
          readOnly: true, // read only with cursor enabled so the component will set cursor to focus on the first matched text.
        }}
        editorDidMount={editor => {
          if (fileContents) {
            //  This is how we go in and highlight the sections of text in the file
            //  which were discovered to be matches.
            const highlightOptions = getHighlightOptions(sources, editor);
            highlightOptions.forEach(({ start, end }) => {
              for (let i = start.line; i <= end.line; i++) {
                editor.addLineClass(i, 'background', styles.highlightedLine);
              }
              editor.markText(start, end, {
                className: styles.matchedText,
              });
            });
            const firstLine = highlightOptions[0]?.start?.line || 0;
            jumpToLineNumber(editor, firstLine);
          }
        }}
      />
    </div>
  );
};

export default FileViewerFileContents;
